import { useEffect, useRef, useState } from 'preact/hooks'
import {
  analyserGraphe,
  grapheDepuisBlocs,
  type GrapheParcours,
  type Bloc,
  type Champ,
} from '@funo/grammaire'
import { CanevasParcours } from './canevas-parcours.tsx'
import { ajusterGraphe } from './graphe-editeur.ts'
import { sourcesParcours } from './parcours-conditions.ts'
import { SallesEditor } from './salles.tsx'
import { LiensSallesInventaire, sallesLieesInventaire } from './liens-salles-inventaire.tsx'
import { OffresEditor } from './offres.tsx'
import type { DonneesInventaire, ItemInventaire, RelationInventaire } from './inventaire-types.ts'
import type { FicheEvenement } from '../../serveur/src/evenements/index.ts'
import {
  apiBibliotheque,
  cleLocale,
  ConditionEditor,
  EditeurChamps,
  nouveauChamp,
  stylesFormulaires,
  type ModeleFormulaire,
} from './formulaires.tsx'

export const NOMS_BLOCS: Record<Bloc['bloc'], string> = {
  identite: 'Coordonnées',
  questions: 'Formulaire',
  creneau: 'Date et réservation',
  offres: 'Équipements et options',
  contrat: 'Contrat',
  signature: 'Signature',
  approbation: 'Validation par l’équipe',
  paiement: 'Paiement',
  depot: 'Dépôt de garantie',
  notification: 'Message automatique',
  confirmation: 'Confirmation',
}
const DESCRIPTIONS: Record<Bloc['bloc'], string> = {
  identite: 'Identifier la personne et ses coordonnées.',
  questions: 'Composer les questions et les choix utiles.',
  creneau: 'Choisir un élément, une date et des heures.',
  offres: 'Ajouter des équipements pour la période réservée.',
  contrat: 'Présenter un document personnalisé à accepter.',
  signature: 'Recueillir la signature du contrat.',
  approbation: 'Confier l’examen à une personne de l’équipe.',
  paiement: 'Encaisser le tarif de la réservation.',
  depot: 'Demander une garantie distincte du tarif.',
  notification: 'Envoyer un message au moment choisi.',
  confirmation: 'Terminer le parcours et indiquer la suite.',
}
const TYPES: Bloc['bloc'][] = [
  'identite',
  'questions',
  'creneau',
  'offres',
  'contrat',
  'signature',
  'approbation',
  'paiement',
  'depot',
  'notification',
  'confirmation',
]
export function creerBloc(type: Bloc['bloc'], fiche: FicheEvenement): Bloc {
  const base = { cle: cleLocale(type), bloc: type, titre: NOMS_BLOCS[type] }
  switch (type) {
    case 'identite':
      return {
        ...base,
        bloc: type,
        demande_telephone: fiche.telephone,
        demande_adresse: false,
        demande_organisation: false,
        champs: [],
      }
    case 'questions':
      return {
        ...base,
        bloc: type,
        titre: 'Quelques précisions',
        champs: [nouveauChamp()],
      }
    case 'creneau':
      return {
        ...base,
        bloc: type,
        titre:
          fiche.type === 'evenement'
            ? 'Votre participation'
            : 'Choisissez votre réservation',
        choix_de_la_ressource:
          fiche.type === 'location' && fiche.ressources.length > 1,
        tenue_minutes: 15,
        quantite_maximum:
          fiche.type === 'evenement' ? fiche.maximum_par_inscription : 1,
      }
    case 'offres':
      return {
        ...base,
        bloc: type,
        options: [],
        ...(fiche.type === 'achat' ? { quantite_principale_maximum: 100 } : {}),
      }
    case 'contrat':
      return {
        ...base,
        bloc: type,
        gabarit: fiche.contrat || '',
        exige_lecture: true,
      }
    case 'signature':
      return {
        ...base,
        bloc: type,
        mode: 'interne',
        mention: 'J’ai lu le contrat et j’en accepte les conditions.',
      }
    case 'approbation':
      return { ...base, bloc: type, delai_heures: 72, bloque_le_creneau: true }
    case 'paiement':
      return { ...base, bloc: type, moment: 'immediat', delai_minutes: 30 }
    case 'depot':
      return { ...base, bloc: type, delai_minutes: 30 }
    case 'notification':
      return {
        ...base,
        bloc: type,
        destinataire: 'citoyen',
        sujet: 'Votre demande',
        corps:
          'Bonjour {{demandeur.nom}},\n\nNous avons reçu votre demande. Merci.',
      }
    case 'confirmation':
      return {
        ...base,
        bloc: type,
        message:
          fiche.type === 'achat'
            ? 'Votre commande est confirmée. Retrouvez les détails dans votre courriel.'
            : fiche.type === 'demande'
              ? 'Votre demande a bien été reçue. Notre équipe vous communiquera la suite.'
              : 'Votre réservation est confirmée. Retrouvez les détails dans votre courriel.',
        joint_calendrier: !['demande', 'achat'].includes(fiche.type),
      }
  }
}
export function parcoursInitial(f: FicheEvenement): Bloc[] {
  if (f.parcours) return f.parcours
  const blocs: Bloc[] = [creerBloc('identite', f)]
  if (f.questions.length)
    blocs.push({
      ...creerBloc('questions', f),
      bloc: 'questions',
      champs: f.questions.map((q) => ({
        ...q,
        options: [],
        sur_lhonneur: false,
      })),
    })
  if (!['demande', 'achat'].includes(f.type))
    blocs.push(creerBloc('creneau', f))
  if (f.type === 'achat') blocs.push(creerBloc('offres', f))
  if (f.contrat) blocs.push(creerBloc('contrat', f), creerBloc('signature', f))
  if (f.depot_cents) blocs.push(creerBloc('depot', f))
  if (f.approbation) blocs.push(creerBloc('approbation', f))
  if (f.prix_cents) blocs.push(creerBloc('paiement', f))
  blocs.push(creerBloc('confirmation', f))
  return blocs
}
function IconeBloc({ type }: { type: Bloc['bloc'] }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="21"
      height="21"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {type === 'identite' ? (
        <>
          <circle cx="12" cy="8" r="3" />
          <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
        </>
      ) : type === 'creneau' ? (
        <>
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M7 3v4m10-4v4M3 11h18m-13 5h3m3 0h2" />
        </>
      ) : type === 'paiement' || type === 'depot' ? (
        <>
          <rect x="2" y="5" width="20" height="14" rx="3" />
          <path d="M2 10h20m-16 5h4" />
        </>
      ) : type === 'notification' ? (
        <>
          <rect x="2" y="5" width="20" height="15" rx="3" />
          <path d="m3 7 9 6 9-6" />
        </>
      ) : type === 'confirmation' || type === 'approbation' ? (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="m7 12 3 3 7-7" />
        </>
      ) : type === 'signature' ? (
        <>
          <path d="m4 16 11-11 4 4L8 20H4v-4ZM14 6l4 4M13 20h8" />
        </>
      ) : (
        <>
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <path d="M9 7h6M9 12h6M9 17h4" />
        </>
      )}
    </svg>
  )
}
function descriptionBloc(b: Bloc, f: FicheEvenement) {
  switch (b.bloc) {
    case 'questions':
      return (
        b.champs.length +
        ' question' +
        (b.champs.length > 1 ? 's' : '') +
        (b.si ? ' · Conditionnel' : '')
      )
    case 'identite':
      return [
        'Nom et courriel',
        b.demande_telephone ? 'téléphone' : '',
        b.demande_organisation ? 'organisation' : '',
      ]
        .filter(Boolean)
        .join(' · ')
    case 'creneau':
      return f.type === 'evenement'
        ? 'Séance à date fixe'
        : f.ressources.length +
            ' espace' +
            (f.ressources.length > 1 ? 's' : '') +
            ((f.subdivisions?.length ?? 0) > 0 ? ' · Salle complète et sections' : ' · Créneaux au choix')
    case 'approbation':
      return 'Examen sous ' + b.delai_heures + ' heures'
    case 'notification':
      return b.destinataire === 'citoyen'
        ? 'À la personne qui réserve'
        : 'À l’équipe'
    default:
      return DESCRIPTIONS[b.bloc]
  }
}
function problemes(blocs: Bloc[], f: FicheEvenement): string[] {
  const p: string[] = []
  const rang = (t: Bloc['bloc']) => blocs.findIndex((b) => b.bloc === t)
  if (!blocs.length) p.push('Ajoutez les étapes de votre parcours.')
  if (rang('confirmation') !== blocs.length - 1 || rang('confirmation') < 0)
    p.push('Une confirmation termine le parcours.')
  if (rang('identite') < 0)
    p.push('Ajoutez les coordonnées pour identifier la personne.')
  if (!['demande', 'achat'].includes(f.type) && rang('creneau') < 0)
    p.push('Ajoutez une étape de réservation pour retenir une place.')
  if (['demande', 'achat'].includes(f.type) && rang('creneau') >= 0)
    p.push('Les achats et demandes libres n’utilisent pas de créneau.')
  if (
    rang('signature') >= 0 &&
    (rang('contrat') < 0 || rang('signature') < rang('contrat'))
  )
    p.push('La signature doit suivre le contrat à signer.')
  const offresPayantes = blocs.some(
    (b) => b.bloc === 'offres' && b.options.some((o) => o.montant_cents > 0),
  )
  if ((f.prix_cents > 0 || offresPayantes) && rang('paiement') < 0)
    p.push('Un tarif est défini : ajoutez l’étape Paiement.')
  if (
    rang('paiement') >= 0 &&
    !['demande', 'achat'].includes(f.type) &&
    (rang('creneau') < 0 || rang('paiement') < rang('creneau'))
  )
    p.push('Placez le paiement après la réservation.')
  blocs.forEach((b, i) => {
    if (b.bloc !== 'offres') return
    if (
      ['paiement', 'contrat', 'signature'].some(
        (type) =>
          rang(type as Bloc['bloc']) >= 0 && i > rang(type as Bloc['bloc']),
      )
    )
      p.push(
        'Placez les équipements et options avant le contrat, la signature et le paiement.',
      )
    if (
      b.options.some(
        (o) => o.ressource_id || ['heure', 'jour'].includes(o.unite),
      ) &&
      (rang('creneau') < 0 || i < rang('creneau'))
    )
      p.push(
        'Les équipements loués demandent une date : placez les options après la réservation.',
      )
  })
  if (
    rang('depot') >= 0 &&
    (!f.depot_cents || rang('creneau') < 0 || rang('depot') < rang('creneau'))
  )
    p.push('Le dépôt demande un montant et une réservation déjà renseignée.')
  for (const b of blocs) {
    if (
      b.bloc === 'questions' &&
      (!b.champs.length ||
        b.champs.some(
          (c) => !c.libelle.trim() || (c.type === 'choix' && !c.options.length),
        ))
    )
      p.push(
        'Complétez les questions et les choix de « ' +
          (b.titre || 'Formulaire') +
          ' ».',
      )
    if (b.bloc === 'contrat' && !b.gabarit.trim())
      p.push('Choisissez un modèle ou rédigez le contrat.')
  }
  return [...new Set(p)]
}

export function ParcoursEditor({
  fiche,
  changer,
  baseUrl = '',
  lecture = false,
  onAuth,
  cleService,
  versionId,
  visible = true,
  onInventaire,
}: {
  fiche: FicheEvenement
  changer(f: FicheEvenement): void
  baseUrl?: string
  lecture?: boolean
  onAuth?(): void
  cleService?: string
  versionId?: string
  visible?: boolean
  onInventaire?(id: string): void
}) {
  const blocs = parcoursInitial(fiche)
  const graphe = fiche.graphe_parcours ?? grapheDepuisBlocs(blocs)
  const analyse = analyserGraphe(blocs, graphe)
  const [pleinEcran, setPleinEcran] = useState(false)
  const [rechercheBlocs, setRechercheBlocs] = useState('')
  const racine = useRef<HTMLDivElement>(null)
  const historique = useRef<{
    avant: FicheEvenement[]
    apres: FicheEvenement[]
  }>({ avant: [], apres: [] })
  const [, setRevisionHistorique] = useState(0)
  const [selection, setSelection] = useState(blocs[0]?.cle ?? ''),
    [catalogue, setCatalogue] = useState(false),
    [modeles, setModeles] = useState<ModeleFormulaire[]>([]),
    [erreur, setErreur] = useState(''),
    [notice, setNotice] = useState(''),
    [occupe, setOccupe] = useState(false)
  const [modeleChoisi, setModeleChoisi] = useState(''),
    [sauverModele, setSauverModele] = useState(false),
    [nomModele, setNomModele] = useState(''),
    [modeleParcours, setModeleParcours] = useState(''),
    [suppression, setSuppression] = useState<string | null>(null)
  const [documents, setDocuments] = useState<
    Array<{ id: string; nom?: string; titre?: string; version_id?: string }>
  >([])
  const [articles, setArticles] = useState<ItemInventaire[]>([])
  const [relations, setRelations] = useState<RelationInventaire[]>([])
  const vivant = useRef(true)
  useEffect(
    () => () => {
      vivant.current = false
    },
    [],
  )
  const dialogue = useRef<HTMLDialogElement>(null)
  const index = blocs.findIndex((b) => b.cle === selection),
    actif = blocs[index]
  const manques = [...new Set([...analyse.erreurs, ...problemes(blocs, fiche)])]
  useEffect(() => {
    if (!blocs.some((b) => b.cle === selection))
      setSelection(blocs[0]?.cle ?? '')
  }, [fiche.parcours])
  useEffect(() => {
    if (!visible) return
    void apiBibliotheque<DonneesInventaire>(baseUrl, 'inventaire')
      .then((r) => { setArticles(r.items); setRelations(r.relations) })
      .catch((e) => {
        if (e.statut === 401) onAuth?.()
      })
    void apiBibliotheque<{ modeles: ModeleFormulaire[] }>(
      baseUrl,
      'formulaires',
    )
      .then((r) => setModeles(r.modeles))
      .catch((e) => {
        if (e.statut === 401) onAuth?.()
      })
    void apiBibliotheque<{
      modeles: Array<{
        id: string
        nom?: string
        titre?: string
        version_id?: string
      }>
    }>(baseUrl, 'documents')
      .then((r) => setDocuments(r.modeles ?? []))
      .catch(() => {})
  }, [baseUrl, visible])
  useEffect(() => {
    if (suppression || modeleParcours) dialogue.current?.showModal()
  }, [suppression, modeleParcours])
  useEffect(() => {
    historique.current = { avant: [], apres: [] }
    setRevisionHistorique((v) => v + 1)
  }, [cleService])
  useEffect(() => {
    if (!visible) setPleinEcran(false)
  }, [visible])
  useEffect(() => {
    if (!pleinEcran) return
    const precedent = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = precedent
    }
  }, [pleinEcran])
  function retenir(suivant: FicheEvenement) {
    historique.current.avant = [
      ...historique.current.avant.slice(-29),
      structuredClone(fiche),
    ]
    historique.current.apres = []
    setRevisionHistorique((v) => v + 1)
    changer(suivant)
  }
  function annuler(refaire = false) {
    if (lecture || occupe) return
    const h = historique.current,
      source = refaire ? h.apres : h.avant,
      cible = refaire ? h.avant : h.apres
    const f = source.pop()
    if (!f) return
    cible.push(structuredClone(fiche))
    setRevisionHistorique((v) => v + 1)
    changer(f)
    setNotice(refaire ? 'Modification rétablie.' : 'Modification annulée.')
  }
  useEffect(() => {
    if (!visible) return
    const clavier = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pleinEcran) {
        setPleinEcran(false)
        return
      }
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return
      const cible = e.target as HTMLElement
      if (
        !racine.current?.contains(cible) ||
        cible.closest('input,textarea,select,[contenteditable=true]')
      )
        return
      e.preventDefault()
      annuler(e.shiftKey)
    }
    window.addEventListener('keydown', clavier)
    return () => window.removeEventListener('keydown', clavier)
  }, [fiche, visible, pleinEcran, lecture, occupe])
  const ecrire = (valeurs: Bloc[], suite: Partial<FicheEvenement> = {}) => {
    const g = suite.graphe_parcours ?? ajusterGraphe(graphe, blocs, valeurs)
    const a = analyserGraphe(valeurs, g)
    const ordonnes = a.complet
      ? a.ordre.map((cle) => valeurs.find((b) => b.cle === cle)!)
      : valeurs
    const approbation = ordonnes.findIndex((b) => b.bloc === 'approbation')
    retenir({
      ...fiche,
      ...suite,
      graphe_parcours: g,
      parcours: ordonnes.map((b, i) =>
        b.bloc === 'paiement'
          ? {
              ...b,
              moment:
                approbation >= 0 && approbation < i
                  ? 'apres_approbation'
                  : 'immediat',
            }
          : b,
      ),
    })
    setErreur('')
    setNotice('')
  }
  const modifier = (partiel: Record<string, unknown>) => {
    const valeurs = blocs.map((b) =>
      b.cle === selection ? ({ ...b, ...partiel } as Bloc) : b,
    )
    if (
      actif?.bloc === 'offres' &&
      valeurs.some(
        (b) =>
          b.bloc === 'offres' && b.options.some((o) => o.montant_cents > 0),
      ) &&
      !valeurs.some((b) => b.bloc === 'paiement')
    ) {
      const index = valeurs.findIndex((b) => b.bloc === 'confirmation')
      valeurs.splice(
        index < 0 ? valeurs.length : index,
        0,
        creerBloc('paiement', fiche),
      )
    }
    ecrire(valeurs)
  }
  function selectionner(cle: string) {
    setSelection(cle)
    setSauverModele(false)
    setModeleChoisi('')
  }
  function ajouter(type: Bloc['bloc'], position?: { x: number; y: number }) {
    if (lecture || occupe) return
    if (blocs.length >= 60) {
      setErreur('Un parcours peut contenir jusqu’à 60 blocs.')
      return
    }
    if (
      !['questions', 'notification', 'offres'].includes(type) &&
      blocs.some((b) => b.bloc === type)
    ) {
      setErreur('Ce bloc est déjà présent dans le parcours.')
      return
    }
    if (
      fiche.type === 'demande' &&
      ['creneau', 'offres', 'paiement', 'depot'].includes(type)
    )
      return
    if (fiche.type === 'achat' && ['creneau', 'depot'].includes(type)) return
    const b = creerBloc(type, fiche)
    if (
      b.bloc === 'offres' &&
      blocs.some((v) => v.bloc === 'offres' && v.quantite_principale_maximum)
    )
      delete b.quantite_principale_maximum
    const origine = graphe.positions[selection] ?? { x: 80, y: 80 }
    const g = {
      ...graphe,
      entree: graphe.entree ?? b.cle,
      positions: {
        ...graphe.positions,
        [b.cle]: position ?? { x: origine.x + 310, y: origine.y + 210 },
      },
    }
    ecrire([...blocs, b], {
      graphe_parcours: g,
      ...(type === 'depot' && !fiche.depot_cents ? { depot_cents: 10000 } : {}),
    })
    selectionner(b.cle)
    setCatalogue(false)
    setNotice('Bloc ajouté. Reliez ses ports pour l’intégrer au parcours.')
  }
  function dupliquer(cle: string) {
    if (lecture || occupe) return
    if (blocs.length >= 60) {
      setErreur('Un parcours peut contenir jusqu’à 60 blocs.')
      return
    }
    const b = blocs.find((b) => b.cle === cle)
    if (!b || !['questions', 'notification'].includes(b.bloc)) return
    const copie = {
      ...structuredClone(b),
      cle: cleLocale(b.bloc),
      titre: (b.titre || NOMS_BLOCS[b.bloc]) + ' (copie)',
    }
    if (copie.bloc === 'questions') {
      const remap = new Map(
        copie.champs.map((c) => [
          'reponses.' + c.cle,
          'reponses.' + cleLocale('question'),
        ]),
      )
      copie.champs = copie.champs.map((c) => ({
        ...c,
        cle: remap.get('reponses.' + c.cle)!.slice(9),
        si: c.si
          ? JSON.parse(
              JSON.stringify(c.si, (_k, v) =>
                typeof v === 'string' ? (remap.get(v) ?? v) : v,
              ),
            )
          : undefined,
      }))
    }
    const p = graphe.positions[cle] ?? { x: 80, y: 80 }
    ecrire([...blocs, copie], {
      graphe_parcours: {
        ...graphe,
        positions: {
          ...graphe.positions,
          [copie.cle]: { x: p.x + 50, y: p.y + 220 },
        },
      },
    })
    selectionner(copie.cle)
    setNotice('Bloc dupliqué. Reliez-le au parcours.')
  }
  function utiliserModele(m: ModeleFormulaire) {
    const copie = structuredClone(m.bloc)
    const utilisees = new Set(
      blocs
        .filter((b) => b.cle !== selection)
        .flatMap((b) =>
          b.bloc === 'questions' || b.bloc === 'identite'
            ? b.champs.map((c) => c.cle)
            : [],
        ),
    )
    const remplacements = new Map<string, string>()
    copie.champs = copie.champs.map((c) => {
      if (!utilisees.has(c.cle)) return c
      const cle = cleLocale('question')
      remplacements.set('reponses.' + c.cle, 'reponses.' + cle)
      return { ...c, cle }
    })
    if (remplacements.size)
      copie.champs = copie.champs.map((c) => ({
        ...c,
        si: c.si
          ? JSON.parse(
              JSON.stringify(c.si, (_k, v) =>
                _k === 'champ' && typeof v === 'string'
                  ? (remplacements.get(v) ?? v)
                  : v,
              ),
            )
          : undefined,
      }))
    modifier({
      champs: copie.champs,
      titre: copie.titre ?? m.nom,
      si: copie.si,
    })
    setModeleChoisi('')
    setNotice(
      'Modèle appliqué. Vos modifications ici restent propres à ce parcours.',
    )
  }
  function appliquerParcours(modele: string) {
    const types: Bloc['bloc'][] =
      modele === 'dossier'
        ? [
            'identite',
            'questions',
            ...(['demande', 'achat'].includes(fiche.type)
              ? fiche.type === 'achat'
                ? (['offres'] as const)
                : []
              : (['creneau'] as const)),
            'approbation',
            ...(fiche.prix_cents ? (['paiement'] as const) : []),
            'confirmation',
          ]
        : modele === 'contrat'
          ? [
              'identite',
              ...(['demande', 'achat'].includes(fiche.type)
                ? fiche.type === 'achat'
                  ? (['offres'] as const)
                  : []
                : (['creneau'] as const)),
              'contrat',
              'signature',
              ...(fiche.prix_cents ? (['paiement'] as const) : []),
              'confirmation',
            ]
          : [
              'identite',
              ...(['demande', 'achat'].includes(fiche.type)
                ? fiche.type === 'achat'
                  ? (['offres'] as const)
                  : []
                : (['creneau'] as const)),
              ...(fiche.prix_cents ? (['paiement'] as const) : []),
              'confirmation',
            ]
    const nouveaux = types.map((t) => {
      const b = creerBloc(t, fiche)
      if (b.bloc === 'offres' && b.quantite_principale_maximum)
        b.quantite_principale_maximum = Math.min(
          1000,
          articles.find((i) => i.id === fiche.inventaire_item_id)?.fiche
            .quantite ?? 100,
        )
      return b
    })
    ecrire(nouveaux, { graphe_parcours: grapheDepuisBlocs(nouveaux) })
    setSelection(nouveaux[0]!.cle)
    setModeleParcours('')
    setNotice('Point de départ appliqué. Vous pouvez réorganiser chaque étape.')
  }
  async function verifier() {
    setOccupe(true)
    setErreur('')
    try {
      const r = await apiBibliotheque<{
        valide: boolean
        manques: Array<{ message?: string }>
      }>(baseUrl, 'evenements/valider', 'POST', {
        fiche: { ...fiche, parcours: blocs },
        ...(cleService ? { cle: cleService } : {}),
        ...(versionId ? { version_id: versionId } : {}),
      })
      if (r.valide)
        setNotice(
          'Le parcours est cohérent. Vous pouvez le tester avant publication.',
        )
      else
        setErreur(
          r.manques
            .map((m) => m.message)
            .filter(Boolean)
            .join(' '),
        )
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupe(false)
    }
  }
  async function enregistrerModele() {
    if (actif?.bloc !== 'questions' || !nomModele.trim()) return
    setOccupe(true)
    try {
      await apiBibliotheque(baseUrl, 'formulaires', 'POST', {
        nom: nomModele.trim(),
        description: 'Modèle créé depuis un parcours.',
        bloc: actif,
      })
      const r = await apiBibliotheque<{ modeles: ModeleFormulaire[] }>(
        baseUrl,
        'formulaires',
      )
      setModeles(r.modeles)
      setSauverModele(false)
      setNotice('Votre formulaire est disponible dans la bibliothèque.')
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupe(false)
    }
  }
  async function choisirDocument(id: string) {
    if (!id) return
    setOccupe(true)
    setErreur('')
    try {
      const r = await apiBibliotheque<{
        bloc: Extract<Bloc, { bloc: 'contrat' }>
      }>(baseUrl, 'documents/modeles/' + encodeURIComponent(id))
      if (!vivant.current) return
      modifier({
        gabarit: r.bloc.gabarit,
        document: r.bloc.document,
        exige_lecture: r.bloc.exige_lecture,
      })
      setNotice('Cette version du contrat est jointe au parcours.')
    } catch (e) {
      setErreur((e as Error).message)
    } finally {
      setOccupe(false)
    }
  }
  return (
    <div
      class={'wf-root wf-studio' + (pleinEcran ? ' wf-plein-ecran' : '')}
      ref={racine}
    >
      <style>{stylesFormulaires + stylesParcours}</style>
      <fieldset disabled={occupe}>
        <div class="wf-outils">
          <div>
            <strong>Votre parcours, sur le canevas</strong>
            <p>
              Déplacez les blocs et reliez leurs ports. Sélectionnez un bloc
              pour le configurer.
            </p>
          </div>
          <div class="wf-outils-actions">
            <button
              type="button"
              class="fb-bouton wf-historique"
              aria-label="Annuler la dernière modification"
              disabled={lecture || occupe || !historique.current.avant.length}
              onClick={() => annuler()}
            >
              ↶
            </button>
            <button
              type="button"
              class="fb-bouton wf-historique"
              aria-label="Rétablir la modification"
              disabled={lecture || occupe || !historique.current.apres.length}
              onClick={() => annuler(true)}
            >
              ↷
            </button>
            <button
              type="button"
              class="fb-bouton wf-agrandir"
              onClick={() => setPleinEcran(!pleinEcran)}
            >
              {pleinEcran ? 'Réduire' : 'Agrandir'}
            </button>
            <select
              aria-label="Partir d’un modèle de parcours"
              value=""
              disabled={lecture}
              onChange={(e) => setModeleParcours(e.currentTarget.value)}
            >
              <option value="">Modèles de parcours</option>
              <option value="direct">Réservation directe</option>
              <option value="dossier">Dossier à étudier</option>
              <option value="contrat">Avec contrat à signer</option>
            </select>
            <button
              type="button"
              class="fb-bouton fb-primaire"
              disabled={lecture}
              onClick={() => setCatalogue(!catalogue)}
            >
              + Ajouter un bloc
            </button>
          </div>
        </div>
        {erreur ? (
          <p class="fb-erreur" role="alert">
            {erreur}
          </p>
        ) : null}
        {notice ? (
          <p class="wf-notice" role="status">
            {notice}
          </p>
        ) : null}
        <div class="wf-workspace">
          <div class="wf-zone-canevas">
            <CanevasParcours
              blocs={blocs}
              graphe={graphe}
              selection={selection}
              lecture={lecture || occupe}
              onSelection={selectionner}
              onGraphe={(g) => ecrire(blocs, { graphe_parcours: g })}
              onAjouter={ajouter}
              onSupprimer={(cle) => setSuppression(cle)}
              onDupliquer={dupliquer}
              noms={NOMS_BLOCS}
              description={(b) => descriptionBloc(b, fiche)}
              icone={(type) => <IconeBloc type={type} />}
            />
            {catalogue ? (
              <div class="wf-catalogue wf-bibliotheque">
                <div>
                  <h3>Bibliothèque de blocs</h3>
                  <button
                    type="button"
                    class="fb-lien"
                    onClick={() => setCatalogue(false)}
                  >
                    Fermer
                  </button>
                </div>
                <p class="wf-aide">
                  Glissez un bloc sur le canevas, puis reliez-le.
                </p>
                <input
                  type="search"
                  aria-label="Rechercher un bloc"
                  placeholder="Formulaire, contrat, paiement…"
                  value={rechercheBlocs}
                  onInput={(e) => setRechercheBlocs(e.currentTarget.value)}
                />
                <div class="wf-types">
                  {TYPES.filter(
                    (t) =>
                      (fiche.type !== 'demande' ||
                        !['creneau', 'offres', 'paiement', 'depot'].includes(
                          t,
                        )) &&
                      (fiche.type !== 'achat' ||
                        !['creneau', 'depot'].includes(t)) &&
                      (NOMS_BLOCS[t] + ' ' + DESCRIPTIONS[t])
                        .toLocaleLowerCase()
                        .includes(rechercheBlocs.toLocaleLowerCase()),
                  ).map((t) => {
                    const existe =
                      !['questions', 'notification', 'offres'].includes(t) &&
                      blocs.some((b) => b.bloc === t)
                    return (
                      <button
                        type="button"
                        key={t}
                        disabled={existe}
                        draggable={!existe && !lecture}
                        onDragStart={(e) => {
                          e.dataTransfer?.setData(
                            'application/x-funo-bloc',
                            t,
                          )
                          if (e.dataTransfer)
                            e.dataTransfer.effectAllowed = 'copy'
                        }}
                        onClick={() => ajouter(t)}
                      >
                        <span class={'wf-icone wf-icone-' + t}>
                          <IconeBloc type={t} />
                        </span>
                        <span>
                          <strong>{NOMS_BLOCS[t]}</strong>
                          <small>
                            {existe ? 'Déjà dans le parcours' : DESCRIPTIONS[t]}
                          </small>
                        </span>
                        <span>+</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </div>
          <div class="wf-inspecteur">
            {actif ? (
              <>
                <div class="wf-inspecteur-entete">
                  <span class={'wf-icone wf-icone-' + actif.bloc}>
                    <IconeBloc type={actif.bloc} />
                  </span>
                  <div>
                    <small>
                      Étape {index + 1} · {NOMS_BLOCS[actif.bloc]}
                    </small>
                    <h3>{actif.titre || NOMS_BLOCS[actif.bloc]}</h3>
                    {!lecture ? (
                      <div class="wf-mini-actions">
                        {['questions', 'notification'].includes(actif.bloc) ? (
                          <button
                            type="button"
                            class="fb-lien"
                            onClick={() => dupliquer(actif.cle)}
                          >
                            Dupliquer
                          </button>
                        ) : null}
                        <button
                          type="button"
                          class="fb-lien"
                          onClick={() => setSuppression(actif.cle)}
                        >
                          Retirer
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
                <fieldset class="wf-reglages" disabled={lecture || occupe}>
                  <label>
                    Titre présenté au public
                    <input
                      value={actif.titre ?? ''}
                      onInput={(e) =>
                        modifier({ titre: e.currentTarget.value })
                      }
                      placeholder={NOMS_BLOCS[actif.bloc]}
                    />
                  </label>
                  {actif.bloc === 'identite' ? (
                    <>
                      <p class="wf-aide">
                        Le nom et le courriel identifient la réservation.
                        Choisissez les coordonnées supplémentaires. Le nom de
                        l’entreprise peut être laissé vide pour un particulier.
                      </p>
                      <div class="wf-cases">
                        <label>
                          <input
                            type="checkbox"
                            checked={actif.demande_organisation}
                            onChange={(e) =>
                              modifier({
                                demande_organisation: e.currentTarget.checked,
                              })
                            }
                          />
                          Entreprise ou organisme (facultatif)
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={actif.demande_telephone}
                            onChange={(e) =>
                              modifier({
                                demande_telephone: e.currentTarget.checked,
                              })
                            }
                          />
                          Téléphone
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={actif.demande_adresse}
                            onChange={(e) =>
                              modifier({
                                demande_adresse: e.currentTarget.checked,
                              })
                            }
                          />
                          Adresse
                        </label>
                      </div>
                    </>
                  ) : null}
                  {actif.bloc === 'questions' ? (
                    <div class="wf-modele-form">
                      <label>
                        Utiliser un modèle de formulaire
                        <select
                          aria-label="Modèle de formulaire"
                          value={modeleChoisi}
                          onChange={(e) =>
                            setModeleChoisi(e.currentTarget.value)
                          }
                        >
                          <option value="">
                            Choisir dans la bibliothèque…
                          </option>
                          {modeles.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.nom}
                              {m.origine === 'organisation'
                                ? ' · Votre modèle'
                                : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                      {modeleChoisi ? (
                        <div class="wf-appliquer-modele">
                          <p>
                            Les questions de cette étape seront remplacées par
                            une copie du modèle. Les autres étapes restent en
                            place.
                          </p>
                          <button
                            type="button"
                            class="fb-bouton"
                            onClick={() => {
                              const m = modeles.find(
                                (m) => m.id === modeleChoisi,
                              )
                              if (m) utiliserModele(m)
                            }}
                          >
                            Appliquer ce formulaire
                          </button>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        class="fb-lien"
                        onClick={() => {
                          setNomModele(actif.titre ?? 'Mon formulaire')
                          setSauverModele(!sauverModele)
                        }}
                      >
                        Enregistrer ce formulaire comme modèle
                      </button>
                      {sauverModele ? (
                        <div class="wf-sauver-modele">
                          <label>
                            Nom dans la bibliothèque
                            <input
                              value={nomModele}
                              onInput={(e) =>
                                setNomModele(e.currentTarget.value)
                              }
                            />
                          </label>
                          <button
                            type="button"
                            class="fb-bouton"
                            disabled={!nomModele.trim() || occupe}
                            onClick={() => void enregistrerModele()}
                          >
                            Enregistrer le modèle
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {actif.bloc === 'creneau' ? (
                    <>
                      <p class="wf-aide">
                        {fiche.type === 'evenement'
                          ? 'La personne confirme sa participation à la séance définie dans Disponibilités.'
                          : 'Configurez les salles et leurs subdivisions ici. Les horaires se règlent dans l’onglet Disponibilités.'}{' '}
                        Vous choisissez à quel moment cette étape intervient.
                      </p>
                      {fiche.type === 'location' ? <>
                        {fiche.inventaire_item_id ? (
                          <div class="wf-salles-liees">
                            <strong>Salles et espaces liés</strong>
                            {relations.filter((r) => r.item_a === fiche.inventaire_item_id || r.item_b === fiche.inventaire_item_id).length ? (
                              <ul>{relations.filter((r) => r.item_a === fiche.inventaire_item_id || r.item_b === fiche.inventaire_item_id).map((relation) => {
                                const id = relation.item_a === fiche.inventaire_item_id ? relation.item_b : relation.item_a
                                return <li key={relation.id}>{articles.find((i) => i.id === id)?.fiche.nom ?? 'Espace lié'} · {relation.mode === 'journee' ? 'Journée entière' : 'Heures qui se chevauchent'}</li>
                              })}</ul>
                            ) : <p class="wf-aide">Aucune subdivision liée à cet espace.</p>}
                            <p class="wf-aide">Réserver cet espace rend ses espaces liés indisponibles, et inversement. Configurez la salle complète et chaque section dans l’inventaire.</p>
                            {onInventaire ? <button type="button" class="fb-lien" onClick={() => onInventaire(fiche.inventaire_item_id!)}>Configurer les subdivisions dans l’inventaire ↗</button> : null}
                          </div>
                        ) : (
                          <>
                            <SallesEditor fiche={fiche}
                              sallesLiees={sallesLieesInventaire(fiche.ressources, articles, relations)}
                              changer={(espaces) => ecrire(
                                blocs.map((b) => b.bloc === 'creneau' ? { ...b, choix_de_la_ressource: espaces.ressources.length > 1 } : b),
                                espaces,
                              )} />
                            <LiensSallesInventaire ressources={fiche.ressources} articles={articles}
                              relations={relations} onInventaire={onInventaire} />
                          </>
                        )}
                        {fiche.ressources.length > 1 ? <label class="fb-case">
                          <input type="checkbox" checked={actif.choix_de_la_ressource}
                            onChange={(e) => modifier({ choix_de_la_ressource: e.currentTarget.checked })} />
                          Laisser choisir la salle complète ou une section
                        </label> : null}
                      </> : null}
                      <div class="fb-deux">
                        <label>
                          Temps laissé pour compléter la demande (minutes)
                          <input
                            type="number"
                            min={1}
                            max={1440}
                            value={actif.tenue_minutes}
                            onInput={(e) =>
                              modifier({
                                tenue_minutes: Number(e.currentTarget.value),
                              })
                            }
                          />
                        </label>
                        {fiche.type === 'evenement' ? (
                          <label>
                            Personnes par inscription
                            <input
                              type="number"
                              min={1}
                              max={fiche.capacite}
                              value={fiche.maximum_par_inscription}
                              onInput={(e) =>
                                ecrire(blocs, {
                                  maximum_par_inscription: Number(
                                    e.currentTarget.value,
                                  ),
                                })
                              }
                            />
                          </label>
                        ) : (
                          <label>
                            Quantité maximale par réservation
                            <input
                              type="number"
                              min={1}
                              max={Math.min(
                                1000,
                                articles.find(
                                  (i) => i.id === fiche.inventaire_item_id,
                                )?.fiche.quantite ?? 1,
                              )}
                              value={actif.quantite_maximum ?? 1}
                              onInput={(e) =>
                                modifier({
                                  quantite_maximum: Number(
                                    e.currentTarget.value,
                                  ),
                                })
                              }
                            />
                          </label>
                        )}
                      </div>
                    </>
                  ) : null}
                  {actif.bloc === 'contrat' ? (
                    <>
                      <label>
                        Document à utiliser
                        <select
                          aria-label="Modèle de contrat"
                          value=""
                          onChange={(e) =>
                            void choisirDocument(e.currentTarget.value)
                          }
                        >
                          <option value="">
                            Choisir un contrat dans Documents…
                          </option>
                          {documents.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.nom ?? d.titre ?? 'Contrat'}
                            </option>
                          ))}
                        </select>
                      </label>
                      {!documents.length ? (
                        <p class="wf-aide">
                          Créez un modèle dans la section Documents pour ajouter
                          votre logo, vos couleurs et vos clauses.
                        </p>
                      ) : null}
                      <label>
                        Texte du contrat
                        <textarea
                          rows={10}
                          value={actif.gabarit}
                          onInput={(e) =>
                            modifier({ gabarit: e.currentTarget.value })
                          }
                          placeholder="Choisissez un document ou écrivez vos conditions."
                        />
                      </label>
                      <p class="wf-aide">
                        Les variables du modèle seront remplacées par les
                        renseignements de la demande. Ajoutez une étape
                        Signature après ce contrat pour le faire signer.
                      </p>
                      <label class="fb-case">
                        <input
                          type="checkbox"
                          checked={actif.exige_lecture}
                          onChange={(e) =>
                            modifier({ exige_lecture: e.currentTarget.checked })
                          }
                        />
                        Demander l’acceptation des conditions
                      </label>
                    </>
                  ) : null}
                  {actif.bloc === 'signature' ? (
                    <>
                      <p class="wf-aide">
                        La signature porte sur le contrat accepté plus tôt dans
                        ce parcours. Le document et sa version sont conservés
                        avec la réservation.
                      </p>
                      <label>
                        Mention de consentement
                        <textarea
                          rows={4}
                          value={actif.mention}
                          onInput={(e) =>
                            modifier({ mention: e.currentTarget.value })
                          }
                        />
                      </label>
                    </>
                  ) : null}
                  {actif.bloc === 'approbation' ? (
                    <>
                      <label>
                        Adresse de la personne responsable
                        <input
                          type="email"
                          value={actif.responsable_courriel ?? ''}
                          placeholder="Adresse de l’organisation si laissé vide"
                          onInput={(e) =>
                            modifier({
                              responsable_courriel:
                                e.currentTarget.value || undefined,
                            })
                          }
                        />
                      </label>
                      <label>
                        Délai pour examiner la demande (heures)
                        <input
                          type="number"
                          min={1}
                          value={actif.delai_heures}
                          onInput={(e) =>
                            modifier({
                              delai_heures: Number(e.currentTarget.value),
                            })
                          }
                        />
                      </label>
                      {!['demande', 'achat'].includes(fiche.type) ? (
                        <label class="fb-case">
                          <input
                            type="checkbox"
                            checked={actif.bloque_le_creneau}
                            onChange={(e) =>
                              modifier({
                                bloque_le_creneau: e.currentTarget.checked,
                              })
                            }
                          />
                          Garder le créneau pendant l’examen
                        </label>
                      ) : null}
                      <p class="wf-aide">
                        L’équipe prend sa décision dans Réservations. La
                        personne reprend ensuite à l’étape suivante.
                      </p>
                    </>
                  ) : null}
                  {actif.bloc === 'offres' ? (
                    <OffresEditor
                      bloc={actif}
                      changer={(b) => modifier(b)}
                      articles={articles}
                      fiche={fiche}
                      champs={sourcesParcours(blocs.slice(0, index), fiche)}
                      onInventaire={onInventaire}
                    />
                  ) : null}
                  {actif.bloc === 'paiement' || actif.bloc === 'depot' ? (
                    <>
                      <label>
                        {actif.bloc === 'depot'
                          ? 'Montant du dépôt ($ CA)'
                          : fiche.type === 'evenement'
                            ? 'Prix par personne ($ CA)'
                            : fiche.inventaire_item_id
                              ? 'Tarif de l’élément ($ CA)'
                              : 'Prix par heure ($ CA)'}
                        <input
                          readOnly={
                            actif.bloc === 'paiement' &&
                            !!fiche.inventaire_item_id
                          }
                          type="number"
                          min={0}
                          step="0.01"
                          value={
                            (actif.bloc === 'depot'
                              ? fiche.depot_cents
                              : fiche.prix_cents) / 100
                          }
                          onInput={(e) =>
                            ecrire(blocs, {
                              [actif.bloc === 'depot'
                                ? 'depot_cents'
                                : 'prix_cents']: Math.round(
                                Number(e.currentTarget.value) * 100,
                              ),
                            })
                          }
                        />
                      </label>
                      <label>
                        Délai de paiement (minutes)
                        <input
                          type="number"
                          min={1}
                          value={actif.delai_minutes}
                          onInput={(e) =>
                            modifier({
                              delai_minutes: Number(e.currentTarget.value),
                            })
                          }
                        />
                      </label>
                      <p class="wf-aide">
                        {actif.bloc === 'depot'
                          ? 'Le dépôt est distinct du tarif. Son encaissement ou sa retenue suit les règles de garantie du service.'
                          : 'Le total comprend l’élément réservé et ses options, selon la période et les quantités choisies. Les tarifs et comptes GL proviennent de l’inventaire.'}
                      </p>
                    </>
                  ) : null}
                  {actif.bloc === 'notification' ? (
                    <>
                      <label>
                        Destinataire
                        <select
                          value={actif.destinataire}
                          onChange={(e) =>
                            modifier({ destinataire: e.currentTarget.value })
                          }
                        >
                          <option value="citoyen">
                            La personne qui réserve
                          </option>
                          <option value="responsable">
                            La personne responsable
                          </option>
                        </select>
                      </label>
                      <label>
                        Objet du message
                        <input
                          value={actif.sujet}
                          onInput={(e) =>
                            modifier({ sujet: e.currentTarget.value })
                          }
                        />
                      </label>
                      <label>
                        Message
                        <textarea
                          rows={6}
                          value={actif.corps}
                          onInput={(e) =>
                            modifier({ corps: e.currentTarget.value })
                          }
                        />
                      </label>
                      <details>
                        <summary>Programmer par rapport à l’activité</summary>
                        <label>
                          Décalage en heures
                          <input
                            type="number"
                            value={actif.decalage_heures ?? ''}
                            placeholder="Vide : à cette étape du parcours"
                            onInput={(e) =>
                              modifier({
                                decalage_heures:
                                  e.currentTarget.value === ''
                                    ? undefined
                                    : Number(e.currentTarget.value),
                              })
                            }
                          />
                        </label>
                        <p class="wf-aide">
                          Un nombre négatif programme un rappel avant le début
                          de l’activité.
                        </p>
                      </details>
                    </>
                  ) : null}
                  {actif.bloc === 'confirmation' ? (
                    <>
                      <label>
                        Message de confirmation
                        <textarea
                          rows={5}
                          value={actif.message}
                          onInput={(e) =>
                            modifier({ message: e.currentTarget.value })
                          }
                        />
                      </label>
                      {!['demande', 'achat'].includes(fiche.type) ? (
                        <label class="fb-case">
                          <input
                            type="checkbox"
                            checked={actif.joint_calendrier}
                            onChange={(e) =>
                              modifier({
                                joint_calendrier: e.currentTarget.checked,
                              })
                            }
                          />
                          Joindre un rendez-vous au calendrier
                        </label>
                      ) : null}
                      <p class="wf-aide">
                        Cette étape termine le parcours. Les formulaires, la
                        signature et les paiements doivent la précéder.
                      </p>
                    </>
                  ) : null}
                  {!['identite', 'confirmation'].includes(actif.bloc) ? (
                    <ConditionEditor
                      valeur={actif.si}
                      champs={sourcesParcours(blocs.slice(0, index), fiche)}
                      changer={(si) => modifier({ si })}
                    />
                  ) : null}
                </fieldset>
                {actif.bloc === 'questions' || actif.bloc === 'identite' ? (
                  <div class="wf-formulaire-imbrique">
                    <EditeurChamps
                      champs={actif.champs}
                      espace={
                        actif.bloc === 'identite' ? 'demandeur' : 'reponses'
                      }
                      changer={(champs) => modifier({ champs })}
                      precedents={sourcesParcours(blocs.slice(0, index), fiche)}
                      titre={actif.titre}
                      lecture={lecture || occupe}
                    />
                  </div>
                ) : null}
              </>
            ) : (
              <div class="fb-vide">
                <h3>Choisissez une étape</h3>
                <p>
                  Ajoutez les étapes nécessaires, puis ajustez leurs paramètres
                  ici.
                </p>
              </div>
            )}
          </div>
        </div>
        <div class="wf-validation">
          <div>
            <strong>
              {manques.length
                ? 'Quelques points à compléter'
                : 'Votre parcours est prêt à être vérifié'}
            </strong>
            {manques.length ? (
              <ul>
                {manques.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            ) : (
              <p>
                Vérifiez les dépendances puis essayez le parcours avant de
                l’ouvrir au public.
              </p>
            )}
          </div>
          <button
            type="button"
            class="fb-bouton"
            disabled={occupe}
            onClick={() => void verifier()}
          >
            {occupe ? 'Vérification…' : 'Vérifier le parcours'}
          </button>
        </div>
        {suppression || modeleParcours ? (
          <dialog
            ref={dialogue}
            class="fb-dialogue"
            onCancel={() => {
              setSuppression(null)
              setModeleParcours('')
            }}
            aria-label={
              suppression ? 'Retirer une étape' : 'Remplacer le parcours'
            }
          >
            <h2>
              {suppression
                ? 'Retirer cette étape ?'
                : 'Utiliser ce point de départ ?'}
            </h2>
            <p>
              {suppression
                ? 'Les autres étapes gardent leur ordre. Les anciennes réservations continuent avec leur propre version du parcours.'
                : 'Les étapes actuelles seront remplacées. Les paramètres de votre événement, les documents enregistrés et les modèles de formulaires restent disponibles.'}
            </p>
            <div class="fb-actions">
              <button
                type="button"
                class="fb-bouton"
                onClick={() => {
                  setSuppression(null)
                  setModeleParcours('')
                }}
              >
                Conserver mon parcours
              </button>
              <button
                type="button"
                class="fb-bouton fb-primaire"
                onClick={() => {
                  if (suppression) {
                    ecrire(blocs.filter((b) => b.cle !== suppression))
                    setSuppression(null)
                  } else appliquerParcours(modeleParcours)
                }}
              >
                {suppression ? 'Retirer l’étape' : 'Appliquer le modèle'}
              </button>
            </div>
          </dialog>
        ) : null}
      </fieldset>
    </div>
  )
}

export const stylesParcours = `
.wf-reglages .sa-editor{margin:22px 0;padding-top:18px;border-top:1px solid #e4e4eb}.wf-reglages .sa-salle{flex-direction:column;align-items:stretch;gap:0}.wf-reglages .sa-salle label{margin-bottom:8px}.wf-reglages .sa-actions{justify-content:space-between}.wf-salles-liees{padding:15px;border:1px solid #e4e4eb;border-radius:12px;margin-bottom:20px}.wf-salles-liees ul{padding-left:18px;font-size:12px;line-height:1.7}

.wf-root{color:#2a1632;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;min-width:0}.wf-root *{box-sizing:border-box}.wf-root h3,.wf-root p{margin:0}.wf-root fieldset{border:0;padding:0;margin:0;min-width:0}.wf-root input:not([type=checkbox]),.wf-root select,.wf-root textarea{font:inherit;border:1px solid #dcd3df;border-radius:10px;background:white;color:#2a1632;min-height:44px;padding:11px 12px;width:100%;min-width:0}.wf-root textarea{resize:vertical}.wf-root label:not(.fb-case){display:flex;flex-direction:column;gap:7px;font-size:13px;margin-bottom:18px}.wf-root input[type=checkbox]{width:18px!important;min-height:18px!important;height:18px!important;accent-color:#391d44}.wf-outils{display:flex;align-items:center;justify-content:space-between;gap:22px;margin-bottom:22px}.wf-outils strong{font-size:17px}.wf-outils p{font-size:12px;color:#6b5a70;margin-top:6px;max-width:470px}.wf-outils-actions{display:flex;gap:10px;flex-shrink:0}.wf-outils-actions select{font-size:12px;width:195px}.wf-outils-actions .fb-bouton{white-space:nowrap}.wf-grille{display:grid;grid-template-columns:260px minmax(0,1fr);gap:23px;align-items:start}.wf-sequence{min-width:0}.wf-debut{font-size:11px;color:#826d8a;margin:5px 0 16px 12px!important;display:flex;gap:8px;align-items:center}.wf-debut>span{width:7px;height:7px;border:2px solid #b1b2e8;border-radius:50%}.wf-sequence ol{list-style:none;padding:0;margin:0}.wf-sequence li{border:1px solid #e3dce6;border-radius:12px;background:#fff;position:relative;margin-bottom:21px;box-shadow:0 2px 5px #00000002}.wf-sequence li:after{content:'';position:absolute;width:1px;height:21px;bottom:-22px;left:27px;background:#e5d5ef}.wf-sequence li:last-child:after{display:none}.wf-sequence .wf-selectionnee{border-color:#9c9ee2;box-shadow:0 0 0 3px #391d4410}.wf-etape{display:flex!important;gap:11px;align-items:flex-start!important;justify-content:flex-start!important;width:100%;padding:16px 13px!important;background:none!important;color:#272129!important;border:0!important;text-align:left!important}.wf-etape>span:nth-child(2){flex:1;min-width:0}.wf-etape small{display:block;font-size:10px;color:#856f8e;margin-bottom:4px}.wf-etape strong{font-size:13px;display:block;line-height:1.4;overflow-wrap:anywhere}.wf-etape em{font-size:10px;font-style:normal;color:#7f6b88;display:block;margin-top:6px;line-height:1.45}.wf-icone{display:grid;place-items:center;width:35px;height:35px;flex-shrink:0;border-radius:9px;background:#f7f1fa;color:#391d44}.wf-icone-questions{background:#f3ecf8;color:#391d44}.wf-icone-contrat,.wf-icone-signature{background:#f8f1e7;color:#a57a37}.wf-icone-paiement,.wf-icone-depot{background:#edf7f2;color:#408368}.wf-icone-approbation{background:#f9f0e9;color:#ad7a45}.wf-icone-confirmation{background:#ebf6ef;color:#3c8660}.wf-actions-etape{display:flex;align-items:center;gap:7px;padding:5px 10px 9px;border-top:1px solid #f4eff7}.wf-actions-etape>span{flex:1}.wf-actions-etape button{padding:3px 4px;min-height:29px;background:none;border:0;color:#78647f;font-size:10px}.wf-actions-etape button:first-child,.wf-actions-etape button:nth-child(2){font-size:16px;min-width:27px}.wf-actions-etape button:disabled{opacity:.25}.wf-condition-badge{font-size:10px;background:#f4edf8;color:#391d44;border-radius:5px;padding:3px 5px}.wf-ajout-bas{width:100%;border:1px dashed #c6c7ee;border-radius:11px;color:#391d44;font-size:12px;background:#fbf9fd;min-height:42px;padding:10px}.wf-inspecteur{background:white;border:1px solid #e5dfe8;border-radius:15px;min-width:0;overflow:hidden}.wf-inspecteur-entete{padding:22px;display:flex;gap:13px;align-items:center;border-bottom:1px solid #f2ebf5;background:#fefdfe}.wf-inspecteur-entete small{font-size:11px;color:#6b5a70}.wf-inspecteur-entete h3{font-size:18px;margin-top:4px;letter-spacing:-.4px}.wf-root .wf-reglages{padding:23px}.wf-aide{color:#6b5a70;font-size:12px;line-height:1.65;margin:0 0 18px!important}.wf-cases{display:flex;gap:14px;flex-wrap:wrap}.wf-cases label{flex-direction:row!important;align-items:center;gap:8px!important;font-size:12px!important}.wf-cases input{margin:0}.wf-formulaire-imbrique{padding:0 15px 20px}.wf-formulaire-imbrique .fb-editeur-grille{grid-template-columns:minmax(180px,.8fr) minmax(235px,1.1fr)}.wf-formulaire-imbrique .fb-proprietes{padding:18px}.wf-formulaire-imbrique .fb-champs-liste{padding:10px}.wf-formulaire-imbrique .fb-type-icone{display:none}.wf-formulaire-imbrique .fb-types button{font-size:10px}.wf-modele-form{padding:15px;background:#fbf8fc;border:1px solid #efe7f3;border-radius:11px;margin-top:18px}.wf-modele-form label{margin-bottom:8px!important}.wf-modele-form .fb-lien{font-size:12px}.wf-appliquer-modele{padding-top:12px;border-top:1px solid #ebe0ef;margin-top:12px}.wf-appliquer-modele p{font-size:12px;color:#6b5a70;margin-bottom:12px}.wf-sauver-modele{border-top:1px solid #ebe0ef;padding-top:15px;margin-top:10px}.wf-catalogue{background:#fff;border:1px solid #ece0f3;box-shadow:0 6px 22px #391d4410;border-radius:15px;padding:21px;margin-bottom:24px}.wf-catalogue>div:first-child{display:flex;align-items:center;justify-content:space-between;margin-bottom:15px}.wf-catalogue h3{font-size:18px}.wf-types{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}.wf-types>button{display:flex;align-items:center;gap:12px;padding:15px;text-align:left;border:1px solid #ece1f0;background:#fff;border-radius:11px;color:#2a1632;min-height:90px}.wf-types>button:not(:disabled):hover{border-color:#acade7;background:#fdfbfe}.wf-types>button:disabled{opacity:.35}.wf-types>button>span:nth-child(2){flex:1}.wf-types strong{font-size:13px;display:block}.wf-types small{font-size:11px;color:#836d8b;display:block;margin-top:4px;line-height:1.4}.wf-validation{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;border:1px solid #ece1f0;background:#fcfafd;border-radius:12px;padding:20px;margin-top:24px}.wf-validation strong{font-size:13px}.wf-validation ul{font-size:12px;color:#806332;margin:10px 0 0;padding-left:18px}.wf-validation li+li{margin-top:5px}.wf-validation p{font-size:12px;color:#6b5a70;margin-top:7px}.wf-validation .fb-bouton{flex-shrink:0}.wf-notice{padding:12px 15px;background:#edf7f2;border-radius:10px;color:#286148;font-size:12px;margin-bottom:16px!important}.wf-root :focus-visible{outline:3px solid #b9baeb;outline-offset:3px}.wf-root .fb-case{display:flex;flex-direction:row;font-size:13px;line-height:1.6;margin-top:15px}.wf-root summary{cursor:pointer;font-size:12px;color:#6b5a70;margin-bottom:15px}@media(max-width:1250px){.wf-grille{grid-template-columns:230px minmax(0,1fr);gap:17px}.wf-formulaire-imbrique .fb-editeur-grille{grid-template-columns:1fr}.wf-formulaire-imbrique .fb-champs-liste{border-right:0;border-bottom:1px solid #ece1f0}.wf-formulaire-imbrique .fb-deplacer{flex-direction:row}.wf-formulaire-imbrique .fb-types{grid-template-columns:repeat(4,1fr)}.wf-outils{align-items:flex-start;flex-direction:column}.wf-types{grid-template-columns:repeat(2,1fr)}}@media(max-width:720px){.wf-grille{grid-template-columns:1fr}.wf-sequence ol{display:flex;overflow-x:auto;gap:10px;padding:3px 3px 12px;scroll-snap-type:x proximity}.wf-sequence li{min-width:245px;max-width:245px;margin:0;scroll-snap-align:start}.wf-sequence li:after{display:none}.wf-actions-etape button{min-height:35px;font-size:12px}.wf-ajout-bas{margin:8px 0}.wf-outils-actions{width:100%;flex-wrap:wrap}.wf-outils-actions select,.wf-outils-actions button{flex:1}.wf-root .wf-reglages{padding:20px}.wf-formulaire-imbrique{padding:0 10px 15px}.wf-formulaire-imbrique .fb-types{grid-template-columns:repeat(2,1fr)}.wf-types{grid-template-columns:1fr}.wf-validation{flex-direction:column}.wf-validation .fb-bouton{width:100%}.wf-root input:not([type=checkbox]),.wf-root select,.wf-root textarea{font-size:16px}.wf-catalogue{padding:15px}.wf-inspecteur-entete{padding:20px}.wf-outils-actions select{font-size:12px}.wf-outils-actions button{font-size:12px}}
.wf-studio .wf-workspace{display:grid;grid-template-columns:minmax(0,1fr) 345px;gap:14px;height:740px;min-height:600px;align-items:stretch}.wf-zone-canevas{position:relative;min-width:0;min-height:0;border:1px solid #eadcf2;border-radius:16px;overflow:hidden;background:#f9f6fc}.wf-studio .wf-inspecteur{overflow:auto;height:100%;border-radius:16px;scrollbar-width:thin}.wf-studio .wf-inspecteur-entete{padding:20px 18px;align-items:flex-start}.wf-studio .wf-inspecteur-entete h3{font-size:17px}.wf-studio .wf-reglages{padding:20px 18px}.wf-studio .wf-outils{gap:18px;flex-direction:row;align-items:center}.wf-studio .wf-outils strong{font-size:20px;letter-spacing:-.5px}.wf-studio .wf-outils p{font-size:12px;max-width:450px}.wf-studio .wf-outils-actions{gap:7px;flex-wrap:wrap;justify-content:flex-end;flex-shrink:1}.wf-studio .wf-outils-actions select{width:160px;min-width:140px;font-size:11px}.wf-studio .wf-outils-actions button{font-size:12px;padding:10px 12px}.wf-studio .wf-historique{min-width:39px;font-size:20px!important;padding:6px!important}.wf-mini-actions{display:flex;gap:13px;margin-top:10px}.wf-mini-actions button{font-size:11px!important}.wf-studio .wf-bibliotheque{position:absolute;top:60px;left:14px;width:290px;max-width:calc(100% - 28px);max-height:calc(100% - 78px);overflow:auto;padding:18px;z-index:8;box-shadow:0 14px 50px #391d4428;margin:0;border-color:#e9dbf1}.wf-bibliotheque h3{font-size:16px}.wf-bibliotheque .wf-types{grid-template-columns:1fr;gap:7px;margin-top:14px}.wf-bibliotheque .wf-types button{min-height:70px;padding:11px}.wf-bibliotheque .wf-types button:not(:disabled){cursor:grab}.wf-bibliotheque .wf-types small{font-size:10px}.wf-bibliotheque .wf-aide{margin-bottom:12px!important}.wf-studio .wf-formulaire-imbrique .fb-editeur-grille{grid-template-columns:1fr}.wf-studio .wf-formulaire-imbrique .fb-types{grid-template-columns:repeat(2,minmax(0,1fr))}.wf-studio .wf-formulaire-imbrique .fb-champs-liste{border-right:0;border-bottom:1px solid #ece1f0}.wf-studio .wf-validation{padding:17px 20px;margin-top:16px}.wf-studio .wf-validation ul{max-height:100px;overflow:auto}.wf-plein-ecran{position:fixed;inset:12px;z-index:90;background:#faf7fc;border:1px solid #eadcf2;border-radius:18px;padding:20px;box-shadow:0 20px 90px #2a163245;display:flex;flex-direction:column}.wf-plein-ecran>fieldset{display:flex;flex-direction:column;flex:1;min-height:0}.wf-plein-ecran .wf-workspace{flex:1;height:auto;min-height:0}.wf-plein-ecran .wf-outils{margin-bottom:16px}.wf-plein-ecran .wf-validation{flex-shrink:0}.wf-plein-ecran .wf-validation ul{max-height:65px}.wf-plein-ecran .wf-zone-canevas>.cv-root{min-height:0!important;height:100%}.wf-plein-ecran .wf-notice,.wf-plein-ecran .fb-erreur{flex-shrink:0}
@media(max-width:1250px){.wf-studio .wf-workspace{grid-template-columns:minmax(0,1fr) 310px}.wf-studio .wf-outils{align-items:flex-start;flex-direction:column}.wf-studio .wf-outils-actions{width:100%;justify-content:flex-start}.wf-plein-ecran .wf-outils{flex-direction:row;align-items:center}.wf-plein-ecran .wf-outils-actions{width:auto}}
@media(max-width:950px){.wf-studio .wf-workspace{grid-template-columns:1fr;height:auto;min-height:0}.wf-zone-canevas{height:540px}.wf-studio .wf-inspecteur{max-height:680px;height:auto}.wf-studio .wf-outils-actions select{flex:1}.wf-plein-ecran{inset:0;border-radius:0;padding:15px;overflow:auto}.wf-plein-ecran>fieldset{display:block;flex:none}.wf-plein-ecran .wf-workspace{display:block}.wf-plein-ecran .wf-zone-canevas{height:65dvh}.wf-plein-ecran .wf-inspecteur{margin-top:14px}.wf-plein-ecran .wf-outils{flex-direction:column;align-items:stretch}.wf-plein-ecran .wf-outils-actions{width:100%}.wf-studio .wf-outils-actions .wf-historique{flex:0 0 39px}.wf-studio .wf-outils-actions .wf-agrandir{flex:0 0 auto}}

`
